import { LocalizationCode } from '../exports';
import { CommonConstants } from './CommonConstants';

/** Represents localization and locale-specific data for a given language/region combination. */
export interface ILocaleData {
  Code: LocalizationCode;
  RTL: boolean;
  // True when the AI voice agent can actually HANDLE CALLS in this language, i.e. Deepgram streaming STT can transcribe it (either Nova 3 'multi' code-switching or a Nova 2 monolingual pin). The
  // authoritative model mapping lives in `src/services/voice/src/modules/TranscriberResolver.ts` (`multiLanguages` ∪ `pinnedLanguages`); this flag MUST stay in sync with that set. Languages Deepgram
  // streaming cannot transcribe at all are `false` here so they never become a tenant's agent language (they would otherwise transcribe as gibberish and break the call). This is a VOICE capability
  // flag ONLY — every locale, supported or not, remains available as a dashboard UI display language.
  AgentSupported: boolean;
}

/** @returns Locale data object list. */
export function GetLocaleList(): ILocaleData[] {
  return localeData;
}

/**
 * Retrieves locale data for a given localization code.
 *
 * @param localizationCode - IETF BCP 47 localization code (e.g., 'en-US')
 * @returns Locale data object if found, null otherwise.
 */
export function GetLocaleData(localizationCode: string): ILocaleData {
  return localeData.find((l) => l.Code === localizationCode) ?? localeData[0];
}

/**
 * Locales the AI voice agent can actually take calls in (Deepgram-transcribable). Use this — NOT {@link GetLocaleList} — to populate any "Agent language" (`tenant.defaultLocale`) picker, and to
 * validate that field server-side, so a tenant can never select a language that would break every call. The dashboard UI language switcher still uses the full {@link GetLocaleList}.
 *
 * @returns The subset of locale data whose `AgentSupported` flag is true.
 */
export function GetLocaleDataForAgents(): ILocaleData[] {
  return localeData.filter((l) => l.AgentSupported);
}

/**
 * Whether a locale is a valid AI voice-agent language (Deepgram-transcribable). Server write paths use this to enforce `tenant.defaultLocale`, coercing anything unsupported to the product
 * default (English) so a call can never be dispatched with an untranscribable agent language.
 *
 * @param localizationCode - The candidate BCP-47 locale (e.g. 'es-MX').
 * @returns True when the locale is present and flagged `AgentSupported`.
 */
export function IsAgentSupportedLocale(localizationCode: string | undefined | null): boolean {
  return !!localizationCode && localeData.some((l) => l.AgentSupported && l.Code === localizationCode);
}

/** Reference to localization codes from CommonConstants for type safety. */
const locCode = CommonConstants.Database.LocalizationCode;

/** File-local constants for GetLocaleData. */
const localConstants = {
  // Region qualifier suffix in LocaleLabels entries ('Spanish (Americas)') stripped when collapsing regional variants into one base-language label.
  labelRegionPattern: / \(.+\)$/,
  // Primary-subtag separator in BCP-47 codes ('es-MX' -> 'es').
  subtagSeparator: '-'
} as const;

/**
 * Comprehensive locale data array containing all supported language/region combinations. `AgentSupported` marks the languages the AI voice agent can take calls in (Deepgram-transcribable — kept
 * in sync with `TranscriberResolver`); the languages flagged `false` (Croatian, Serbian, Urdu, Bengali, Punjabi, Filipino, Arabic, Hebrew, Persian, Swahili, and the Creoles) are
 * Deepgram-untranscribable today, so they are excluded from the Agent-language picker but remain fully available as dashboard UI display languages.
 *
 * @internal
 */
const localeData: ILocaleData[] = [
  { Code: locCode.enUS, RTL: false, AgentSupported: true }, // English (source) - Nova 3 multi

  { Code: locCode.esMX, RTL: false, AgentSupported: true }, // Spanish (Americas) - Nova 3 multi
  { Code: locCode.esES, RTL: false, AgentSupported: true }, // Spanish (Europe) - Nova 3 multi

  { Code: locCode.deDE, RTL: false, AgentSupported: true }, // Germany - Nova 3 multi
  { Code: locCode.frFR, RTL: false, AgentSupported: true }, // France - Nova 3 multi
  { Code: locCode.itIT, RTL: false, AgentSupported: true }, // Italy - Nova 3 multi
  { Code: locCode.nlNL, RTL: false, AgentSupported: true }, // Netherlands - Nova 3 multi
  { Code: locCode.svSE, RTL: false, AgentSupported: true }, // Sweden - Nova 2 pinned
  { Code: locCode.daDK, RTL: false, AgentSupported: true }, // Denmark - Nova 2 pinned
  { Code: locCode.fiFI, RTL: false, AgentSupported: true }, // Finland - Nova 2 pinned
  { Code: locCode.noNO, RTL: false, AgentSupported: true }, // Norway - Nova 2 pinned

  { Code: locCode.elGR, RTL: false, AgentSupported: true }, // Greece - Nova 2 pinned
  { Code: locCode.plPL, RTL: false, AgentSupported: true }, // Poland - Nova 2 pinned
  { Code: locCode.csCZ, RTL: false, AgentSupported: true }, // Czech Republic - Nova 2 pinned
  { Code: locCode.skSK, RTL: false, AgentSupported: true }, // Slovakia - Nova 2 pinned
  { Code: locCode.huHU, RTL: false, AgentSupported: true }, // Hungary - Nova 2 pinned
  { Code: locCode.roRO, RTL: false, AgentSupported: true }, // Romania - Nova 2 pinned
  { Code: locCode.bgBG, RTL: false, AgentSupported: true }, // Bulgaria - Nova 2 pinned
  { Code: locCode.hrHR, RTL: false, AgentSupported: false }, // Croatia - Deepgram streaming unsupported
  { Code: locCode.srRS, RTL: false, AgentSupported: false }, // Serbia - Deepgram streaming unsupported

  { Code: locCode.ukUA, RTL: false, AgentSupported: true }, // Ukraine - Nova 2 pinned
  { Code: locCode.ruRU, RTL: false, AgentSupported: true }, // Russia - Nova 3 multi
  { Code: locCode.trTR, RTL: false, AgentSupported: true }, // Turkey - Nova 2 pinned
  { Code: locCode.ptBR, RTL: false, AgentSupported: true }, // Portuguese (Americas) - Nova 3 multi
  { Code: locCode.ptPT, RTL: false, AgentSupported: true }, // Portuguese (Europe) - Nova 3 multi

  { Code: locCode.zhHans, RTL: false, AgentSupported: true }, // China - Nova 2 pinned
  { Code: locCode.jaJP, RTL: false, AgentSupported: true }, // Japan - Nova 3 multi
  { Code: locCode.koKR, RTL: false, AgentSupported: true }, // South Korea - Nova 2 pinned

  { Code: locCode.hiIN, RTL: false, AgentSupported: true }, // India (Hindi) - Nova 3 multi
  { Code: locCode.urPK, RTL: true, AgentSupported: false }, // Pakistan (Urdu) - Deepgram streaming unsupported
  { Code: locCode.bnBD, RTL: false, AgentSupported: false }, // Bangladesh (Bengali) - Deepgram streaming unsupported
  { Code: locCode.paIN, RTL: false, AgentSupported: false }, // India (Punjabi) - Deepgram streaming unsupported
  { Code: locCode.idID, RTL: false, AgentSupported: true }, // Indonesia - Nova 2 pinned
  { Code: locCode.filPH, RTL: false, AgentSupported: false }, // Philippines (Filipino) - Deepgram streaming unsupported

  { Code: locCode.arSA, RTL: true, AgentSupported: false }, // Saudi Arabia (Arabic) - Deepgram streaming unsupported
  { Code: locCode.heIL, RTL: true, AgentSupported: false }, // Israel (Hebrew) - Deepgram streaming unsupported
  { Code: locCode.faIR, RTL: true, AgentSupported: false }, // Iran (Persian) - Deepgram streaming unsupported

  { Code: locCode.thTH, RTL: false, AgentSupported: true }, // Thailand - Nova 2 pinned
  { Code: locCode.viVN, RTL: false, AgentSupported: true }, // Vietnam - Nova 2 pinned
  { Code: locCode.swKE, RTL: false, AgentSupported: false }, // Kenya (Swahili) - Deepgram streaming unsupported
  { Code: locCode.papCW, RTL: false, AgentSupported: false }, // Curaçao - Deepgram streaming unsupported
  { Code: locCode.htHT, RTL: false, AgentSupported: false }, // Haiti - Deepgram streaming unsupported
  { Code: locCode.crsSC, RTL: false, AgentSupported: false }, // Seychelles - Deepgram streaming unsupported
  { Code: locCode.mfeMU, RTL: false, AgentSupported: false } // Mauritius - Deepgram streaming unsupported
] as const;

/**
 * English baseline display names for every supported language, keyed by the locale code. Each frontend app spreads this map into its `Localized` object so the lobby language selector, the
 * onboarding default-language picker, and the admin portal (tenant default locale, localization status) all render the identical human-friendly language name instead of a raw code. These values
 * are translated alongside the rest of the dictionary, so the lobby selector shows language names in the currently active language. Keeping the single source here guarantees the lobby and portal
 * never drift apart.
 */
export const LocaleLabels: Record<string, string> = {
  [locCode.enUS]: 'English', // English (source)
  [locCode.esMX]: 'Spanish (Americas)', // Latin America
  [locCode.esES]: 'Spanish (Europe)', // Spain
  [locCode.deDE]: 'German', // Germany
  [locCode.frFR]: 'French', // France
  [locCode.itIT]: 'Italian', // Italy
  [locCode.nlNL]: 'Dutch', // Netherlands
  [locCode.svSE]: 'Swedish', // Sweden
  [locCode.daDK]: 'Danish', // Denmark
  [locCode.fiFI]: 'Finnish', // Finland
  [locCode.noNO]: 'Norwegian', // Norway
  [locCode.elGR]: 'Greek', // Greece
  [locCode.plPL]: 'Polish', // Poland
  [locCode.csCZ]: 'Czech', // Czech Republic
  [locCode.skSK]: 'Slovak', // Slovakia
  [locCode.huHU]: 'Hungarian', // Hungary
  [locCode.roRO]: 'Romanian', // Romania
  [locCode.bgBG]: 'Bulgarian', // Bulgaria
  [locCode.hrHR]: 'Croatian', // Croatia
  [locCode.srRS]: 'Serbian', // Serbia
  [locCode.ukUA]: 'Ukrainian', // Ukraine
  [locCode.ruRU]: 'Russian', // Russia
  [locCode.trTR]: 'Turkish', // Turkey
  [locCode.ptBR]: 'Portuguese (Americas)', // Brazil
  [locCode.ptPT]: 'Portuguese (Europe)', // Portugal
  [locCode.zhHans]: 'Chinese', // China
  [locCode.jaJP]: 'Japanese', // Japan
  [locCode.koKR]: 'Korean', // South Korea
  [locCode.hiIN]: 'Hindi', // India
  [locCode.urPK]: 'Urdu', // Pakistan
  [locCode.bnBD]: 'Bengali', // Bangladesh
  [locCode.paIN]: 'Punjabi', // India
  [locCode.idID]: 'Indonesian', // Indonesia
  [locCode.filPH]: 'Filipino', // Philippines
  [locCode.arSA]: 'Arabic', // Saudi Arabia
  [locCode.heIL]: 'Hebrew', // Israel
  [locCode.faIR]: 'Persian', // Iran
  [locCode.thTH]: 'Thai', // Thailand
  [locCode.viVN]: 'Vietnamese', // Vietnam
  [locCode.swKE]: 'Swahili', // Kenya
  [locCode.papCW]: 'Papiamento', // Curaçao
  [locCode.htHT]: 'Haitian Creole', // Haiti
  [locCode.crsSC]: 'Seychellois Creole', // Seychelles
  [locCode.mfeMU]: 'Mauritian Creole' // Mauritius
};

/**
 * The standard agent languages (primary subtags): the Deepgram Nova 3 'multi' code-switching set. These are the only languages where mid-call language switching works end-to-end (the STT
 * code-switches and the default TTS provider covers all of them); every other agent-supported language is "non-standard" (Nova 2 monolingual pin, no mid-call switching). Single source for the
 * voice transcriber resolver, the agent-language picker, and operator tooltips.
 */
export const StandardAgentLanguageSubtags = ['en', 'es', 'fr', 'de', 'hi', 'it', 'ja', 'nl', 'ru', 'pt'] as const;

/**
 * Whether a locale classifies as a standard agent language (the Deepgram Nova 3 'multi' code-switching set, so mid-call language switching works end-to-end).
 *
 * @param localizationCode - The BCP-47 locale to classify (e.g. 'es-MX'); classified by primary subtag, so 'es-MX' and 'es-ES' both classify standard.
 * @returns True when the locale's primary language is in the standard set.
 */
export function IsStandardAgentLocale(localizationCode: string | undefined | null): boolean {
  const subtag = (localizationCode ?? '').split(localConstants.subtagSeparator)[0].trim().toLowerCase();
  return (StandardAgentLanguageSubtags as readonly string[]).includes(subtag);
}

/** Agent-language display labels classified standard vs non-standard. */
export interface IAgentLanguageClassification {
  standard: string[];
  nonStandard: string[];
}

/**
 * The agent-supported languages classified standard vs non-standard, as base-language display labels (regional variants collapsed: 'Spanish (Americas)'/'Spanish (Europe)' -> 'Spanish').
 *
 * @returns The label list per classification.
 */
export function GetAgentLanguageClassification(): IAgentLanguageClassification {
  const standard: string[] = [];
  const nonStandard: string[] = [];
  const seen = new Set<string>();
  for (const entry of GetLocaleDataForAgents()) {
    const label = (LocaleLabels[entry.Code] ?? entry.Code).replace(localConstants.labelRegionPattern, '');
    if (seen.has(label)) continue;
    seen.add(label);
    (IsStandardAgentLocale(entry.Code) ? standard : nonStandard).push(label);
  }
  return { standard, nonStandard };
}
